import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './app/AuthProvider';
import { CollectionProvider } from './app/CollectionProvider';
import { I18nProvider } from './app/I18nProvider';
import { ThemeProvider } from './app/ThemeProvider';
import { TmdbProvider } from './app/TmdbProvider';
import { ToastProvider } from './app/ToastProvider';
import { useAuth, useCollection, useI18n } from './app/context';
import { AppShell } from './components/AppShell';
import { Loading } from './components/Page';
import { isFirebaseConfigured } from './firebase';
import { ConfigError } from './screens/ConfigError';
import { Locked } from './screens/Locked';
import { SignIn } from './screens/SignIn';
import { AddMovie } from './routes/AddMovie';
import { Collection } from './routes/Collection';
import { Insights } from './routes/Insights';
import { MovieDetail } from './routes/MovieDetail';
import { PrintList } from './routes/PrintList';
import { Settings } from './routes/Settings';
import { Wishlist } from './routes/Wishlist';

/**
 * Gate order: configured → signed in → on the allowlist → the app.
 *
 * Whether an account is allowed is not something the client can ask; it is
 * discovered by trying to read and being refused, which is exactly what the
 * security rules decide.
 */
function Gate() {
  const { t } = useI18n();
  const { status, user } = useAuth();

  if (status === 'loading') return <Loading label={t('common.loading')} />;
  if (status === 'signed-out' || !user) return <SignIn />;

  return (
    <CollectionProvider>
      <AllowlistGate />
    </CollectionProvider>
  );
}

function AllowlistGate() {
  const { t } = useI18n();
  const { status } = useCollection();

  if (status === 'denied') return <Locked />;
  if (status === 'loading') return <Loading label={t('auth.loading')} />;

  return (
    <Routes>
      {/* Outside the shell: a printed page should carry no navigation. */}
      <Route path="print" element={<PrintList />} />
      <Route element={<AppShell />}>
        <Route index element={<Collection />} />
        <Route path="movie/:movieId" element={<MovieDetail />} />
        <Route path="add" element={<AddMovie />} />
        <Route path="insights" element={<Insights />} />
        <Route path="wishlist" element={<Wishlist />} />
        <Route path="settings" element={<Settings />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

export function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <ToastProvider>
          {isFirebaseConfigured ? (
            <BrowserRouter>
              <TmdbProvider>
                <AuthProvider>
                  <Gate />
                </AuthProvider>
              </TmdbProvider>
            </BrowserRouter>
          ) : (
            <ConfigError />
          )}
        </ToastProvider>
      </I18nProvider>
    </ThemeProvider>
  );
}
